import React from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  DeliveryDining,
  Restaurant,
  Event,
  LocalOffer,
  Support,
  CheckCircle,
  Schedule,
  Star,
  Fastfood,
  Cake,
  LocalPizza,
  LunchDining,
} from '@mui/icons-material';
import { Service } from '../types';
import HeroSection from './HeroSection';

export default function ServicesPage() {
  const services: Service[] = [
    {
      title: 'Catering Services',
      icon: <Restaurant />,
      description: 'Professional catering for weddings, corporate events, and special occasions.',
      features: ['Custom menus', 'On-site service', 'Dietary accommodations', 'Full setup & cleanup'],
      color: '#d32f2f',
    },
    {
      title: 'Food Delivery',
      icon: <DeliveryDining />,
      description: 'Fast and reliable delivery service bringing our delicious food to your doorstep.',
      features: ['30-minute delivery', 'Real-time tracking', 'Contactless delivery', 'Wide delivery area'],
      color: '#1976d2',
    },
    {
      title: 'Event Planning',
      icon: <Event />,
      description: 'Complete event planning services with food, decoration, and entertainment.',
      features: ['Theme-based events', 'Full coordination', 'Vendor management', 'Budget planning'],
      color: '#388e3c',
    },
    {
      title: 'Private Dining',
      icon: <LocalOffer />,
      description: 'Exclusive private dining experiences for intimate gatherings and business meetings.',
      features: ['Private rooms', 'Personalized service', 'Custom menus', 'Wine pairing'],
      color: '#f57c00',
    },
    {
      title: 'Customer Support',
      icon: <Support />,
      description: '24/7 customer support to ensure your dining experience is always perfect.',
      features: ['24/7 availability', 'Multi-language support', 'Quick resolution', 'Feedback system'],
      color: '#7b1fa2',
    },
    {
      title: 'Loyalty Program',
      icon: <Star />,
      description: 'Rewards program for our valued customers with exclusive benefits and discounts.',
      features: ['Points system', 'Exclusive offers', 'Birthday rewards', 'VIP benefits'],
      color: '#c2185b',
    },
  ];

  const specialServices = [
    {
      name: 'Birthday Parties',
      icon: <Cake />,
      description: 'Make your birthday special with our custom party packages and special birthday treats.',
    },
    {
      name: 'Corporate Lunches',
      icon: <LunchDining />,
      description: 'Professional corporate lunch delivery with bulk ordering and invoicing options.',
    },
    {
      name: 'Family Meals',
      icon: <Fastfood />,
      description: 'Family-sized meal packages perfect for sharing with loved ones at home.',
    },
    {
      name: 'Pizza Parties',
      icon: <LocalPizza />,
      description: 'Pizza delivery for parties and gatherings with various toppings and sizes.',
    },
  ];

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Hero Section */}
      <HeroSection
              title="Our Services"
              description=" Comprehensive dining solutions for every occasion and need"
            />
   
      <Container maxWidth="lg">

        {/* Main Services Grid */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {services.map((service, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 4,
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 4 }}>
                  <Avatar
                    sx={{
                      bgcolor: service.color,
                      width: 64,
                      height: 64,
                      mb: 3,
                      fontSize: 32,
                    }}
                  >
                    {service.icon}
                  </Avatar>
                  <Typography variant="h5" fontWeight={600} gutterBottom>
                    {service.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    {service.description}
                  </Typography>
                  <List dense>
                    {service.features.map((feature, idx) => (
                      <ListItem key={idx} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircle sx={{ color: service.color, fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  </List>
                  <Button
                    variant="contained"
                    fullWidth
                    component={Link}
                    to="/contact"
                    sx={{ mt: 2, bgcolor: service.color }}
                  >
                    Learn More
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Special Services */}
        <Paper sx={{ p: 6, mb: 8, borderRadius: 2 }}>
          <Typography variant="h3" fontWeight={600} align="center" gutterBottom>
            Special Services
          </Typography>
          <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 4 }}>
            Additional services tailored for specific needs
          </Typography>
          <Grid container spacing={3}>
            {specialServices.map((service, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: 'primary.main',
                      width: 56,
                      height: 56,
                      mx: 'auto',
                      mb: 2,
                      fontSize: 28,
                    }}
                  >
                    {service.icon}
                  </Avatar>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {service.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {service.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Service Hours */}
        <Paper sx={{ p: 6, borderRadius: 2 }}>
          <Typography variant="h3" fontWeight={600} align="center" gutterBottom>
            Service Hours
          </Typography>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Avatar
                  sx={{
                    bgcolor: 'primary.main',
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 3,
                    fontSize: 40,
                  }}
                >
                  <Schedule />
                </Avatar>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  Restaurant Hours
                </Typography>
                <List sx={{ maxWidth: 300, mx: 'auto' }}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText primary="Monday - Friday: 11:00 AM - 10:00 PM" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText primary="Saturday: 10:00 AM - 11:00 PM" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText primary="Sunday: 10:00 AM - 9:00 PM" />
                  </ListItem>
                </List>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Avatar
                  sx={{
                    bgcolor: 'secondary.main',
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 3,
                    fontSize: 40,
                  }}
                >
                  <DeliveryDining />
                </Avatar>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  Delivery Hours
                </Typography>
                <List sx={{ maxWidth: 300, mx: 'auto' }}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText primary="Monday - Friday: 11:30 AM - 9:30 PM" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText primary="Saturday: 10:30 AM - 10:30 PM" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText primary="Sunday: 10:30 AM - 8:30 PM" />
                  </ListItem>
                </List>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
}
